import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney, fmtNum } from "@/api";
import { Search, PlusCircle } from "lucide-react";
import BulkImport from "@/pages/BulkImport";

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  client_id: "",
  client_name_input: "",
  account_id: "",
  symbol: "",
  stock_name: "",
  trade_type: "BUY",
  quantity: "",
  price: "",
  commission: "0",
  trade_date: todayISO(),
  notes: "",
};

// Reusable autocomplete input
function AutocompleteInput({ value, onChange, onPick, items, placeholder, testid, itemLabel, itemSublabel, emptyAddon, className = "" }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrapRef = useRef(null);

  const query = (value || "").toLowerCase().trim();
  const suggestions = useMemo(() => {
    if (!query) return items.slice(0, 8);
    return items.filter(it => itemLabel(it).toLowerCase().includes(query) || (itemSublabel?.(it) || "").toLowerCase().includes(query)).slice(0, 8);
  }, [query, items, itemLabel, itemSublabel]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (it) => { onPick(it); setOpen(false); setHi(-1); };

  const onKey = (e) => {
    if (!open) { if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); e.preventDefault(); } return; }
    if (e.key === "ArrowDown") { setHi(h => Math.min(h + 1, suggestions.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHi(h => Math.max(h - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter") {
      if (hi >= 0 && suggestions[hi]) { pick(suggestions[hi]); e.preventDefault(); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <Input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); setHi(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          data-testid={testid}
          autoComplete="off"
          className={`pl-9 ${className}`}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-800 bg-[#0E1524] shadow-xl max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {suggestions.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">
              {query ? "No match." : "Start typing to search…"}
              {emptyAddon && emptyAddon(query)}
            </div>
          ) : (
            <>
              {suggestions.map((it, i) => (
                <button
                  key={itemLabel(it) + i}
                  type="button"
                  onClick={() => pick(it)}
                  onMouseEnter={() => setHi(i)}
                  data-testid={`${testid}-option-${i}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 border-b border-slate-800/60 last:border-0 ${i === hi ? "bg-slate-800/60" : "hover:bg-slate-800/40"}`}
                >
                  <span className="font-semibold text-white truncate">{itemLabel(it)}</span>
                  {itemSublabel && <span className="text-xs text-slate-400 font-mono-tabular shrink-0">{itemSublabel(it)}</span>}
                </button>
              ))}
              {emptyAddon && query && emptyAddon(query)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TradeEntry() {
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // New-stock modal
  const [newStockOpen, setNewStockOpen] = useState(false);
  const [newStock, setNewStock] = useState({ symbol: "", name: "", market_price: "" });

  const load = async () => {
    const [c, s, t] = await Promise.all([api.get("/clients"), api.get("/stocks"), api.get("/transactions?limit=15")]);
    setClients(c.data);
    setStocks(s.data);
    setRecent(t.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!form.client_id) { setAccounts([]); return; }
    (async () => {
      const { data } = await api.get(`/clients/${form.client_id}/accounts`);
      setAccounts(data);
      setForm(f => ({ ...f, account_id: data.length === 1 ? data[0].id : (data.find(a => a.id === f.account_id) ? f.account_id : "") }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const pickClient = (c) => setForm(prev => ({ ...prev, client_id: c.id, client_name_input: c.name, account_id: "" }));

  const pickStock = (s) => setForm(prev => ({
    ...prev,
    symbol: s.symbol,
    stock_name: s.name || prev.stock_name,
    price: prev.price || (s.market_price ? String(s.market_price) : prev.price),
  }));

  const submit = async () => {
    if (!form.client_id) return toast.error("Select a client from the suggestions");
    if (!form.account_id) return toast.error("Select an account");
    if (!form.symbol.trim()) return toast.error("Enter stock symbol");
    const qty = parseFloat(form.quantity);
    const price = parseFloat(form.price);
    if (!qty || qty <= 0) return toast.error("Enter valid quantity");
    if (!price || price < 0) return toast.error("Enter valid price");

    setSubmitting(true);
    try {
      await api.post("/transactions", {
        client_id: form.client_id,
        account_id: form.account_id,
        symbol: form.symbol.toUpperCase().trim(),
        stock_name: form.stock_name,
        trade_type: form.trade_type,
        quantity: qty,
        price,
        commission: parseFloat(form.commission || 0),
        trade_date: form.trade_date,
        notes: form.notes,
      });
      const acctName = accounts.find(a => a.id === form.account_id)?.name || "";
      toast.success(`${form.trade_type} recorded · ${form.symbol.toUpperCase()} · ${acctName}`);
      setForm({ ...EMPTY, client_id: form.client_id, client_name_input: form.client_name_input, account_id: form.account_id, trade_date: form.trade_date });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record trade");
    } finally {
      setSubmitting(false);
    }
  };

  const gross = (parseFloat(form.quantity || 0) * parseFloat(form.price || 0)) || 0;
  const commission = parseFloat(form.commission || 0);
  const netTotal = form.trade_type === "BUY" ? gross + commission : gross - commission;

  const openNewStock = (prefill) => {
    setNewStock({ symbol: (prefill || form.symbol || "").toUpperCase(), name: "", market_price: form.price || "" });
    setNewStockOpen(true);
  };

  const saveNewStock = async () => {
    if (!newStock.symbol.trim()) return toast.error("Symbol required");
    try {
      const { data } = await api.post("/stocks", {
        symbol: newStock.symbol.toUpperCase().trim(),
        name: newStock.name,
        market_price: parseFloat(newStock.market_price) || 0,
      });
      toast.success(`${data.symbol} added to stock master`);
      setStocks(s => [...s, data]);
      setNewStockOpen(false);
      // Auto-select in form
      pickStock(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const symbolExists = stocks.some(s => s.symbol.toUpperCase() === (form.symbol || "").toUpperCase().trim());

  return (
    <div className="space-y-6">
      <header>
        <div className="label-caps">Execution</div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">Daily Trade Entry</h1>
        <p className="text-slate-400 text-sm mt-2">Search clients and stocks by name — auto-suggestions pull from your database.</p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="card-tactical p-6 lg:col-span-8">
          <div className="flex gap-2 mb-6">
            <Button onClick={() => set("trade_type", "BUY")} data-testid="form-trade-type-buy-btn"
              className={`flex-1 h-12 font-bold ${form.trade_type === "BUY" ? "bg-emerald-500 hover:bg-emerald-600 text-slate-950" : "bg-slate-800/60 hover:bg-slate-800 text-slate-300 border border-slate-700"}`}>BUY</Button>
            <Button onClick={() => set("trade_type", "SELL")} data-testid="form-trade-type-sell-btn"
              className={`flex-1 h-12 font-bold ${form.trade_type === "SELL" ? "bg-rose-500 hover:bg-rose-600 text-slate-950" : "bg-slate-800/60 hover:bg-slate-800 text-slate-300 border border-slate-700"}`}>SELL</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client Name * (type to search)">
              <AutocompleteInput
                value={form.client_name_input}
                onChange={(v) => setForm(prev => ({ ...prev, client_name_input: v, client_id: "" }))}
                onPick={pickClient}
                items={clients}
                placeholder="Type name e.g. 'yap'..."
                testid="form-trade-client-input"
                itemLabel={(c) => c.name}
                itemSublabel={(c) => c.phone || c.email || ""}
                className="bg-slate-900/70 border-slate-800 h-11"
              />
              {form.client_id && (
                <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1" data-testid="client-selected-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Selected: {clients.find(c => c.id === form.client_id)?.name}
                </div>
              )}
            </Field>
            <Field label={`Account *${form.client_id ? ` · ${clients.find(c => c.id === form.client_id)?.name}` : ""}`}>
              <Select value={form.account_id} onValueChange={v => set("account_id", v)} disabled={!form.client_id}>
                <SelectTrigger data-testid="form-trade-account-select" className="bg-slate-900/70 border-slate-800 h-11">
                  <SelectValue placeholder={form.client_id ? "Select account" : "Pick a client first"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                  {accounts.map(a => <SelectItem key={a.id} value={a.id} data-testid={`select-account-option-${a.id}`}>{a.name}{a.cds_number ? ` · ${a.cds_number}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Trade Date *">
              <Input type="date" value={form.trade_date} onChange={e => set("trade_date", e.target.value)} data-testid="form-trade-date-input" className="bg-slate-900/70 border-slate-800 h-11" />
            </Field>
            <Field label="Stock Symbol * (type to search)">
              <AutocompleteInput
                value={form.symbol}
                onChange={(v) => setForm(prev => ({ ...prev, symbol: v.toUpperCase() }))}
                onPick={pickStock}
                items={stocks}
                placeholder="Type symbol or company e.g. 'apple'..."
                testid="form-trade-symbol-input"
                itemLabel={(s) => s.symbol}
                itemSublabel={(s) => s.name || ""}
                className="bg-slate-900/70 border-slate-800 h-11 font-mono-tabular uppercase"
                emptyAddon={(q) => (
                  <button
                    type="button"
                    onClick={() => openNewStock(q)}
                    data-testid="btn-inline-add-stock"
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-emerald-400 hover:bg-emerald-500/10 border-t border-slate-800"
                  >
                    <PlusCircle size={14} /> Add "{(q || "new stock").toUpperCase()}" to database
                  </button>
                )}
              />
              {form.symbol && !symbolExists && (
                <div className="text-xs text-amber-400 mt-1" data-testid="stock-not-found-hint">
                  "{form.symbol.toUpperCase()}" not in database.
                  <button type="button" onClick={() => openNewStock()} className="ml-1 underline hover:text-amber-300" data-testid="btn-open-add-stock">Add it now →</button>
                </div>
              )}
            </Field>
            <Field label="Company Name (auto-filled)">
              <Input value={form.stock_name} onChange={e => set("stock_name", e.target.value)} placeholder="Apple Inc." data-testid="form-trade-stock-name-input" className="bg-slate-900/70 border-slate-800 h-11" />
            </Field>
            <Field label="Quantity *">
              <Input type="number" step="any" value={form.quantity} onChange={e => set("quantity", e.target.value)} placeholder="100" data-testid="form-trade-quantity-input" className="bg-slate-900/70 border-slate-800 h-11 font-mono-tabular" />
            </Field>
            <Field label="Price per Share *">
              <Input type="number" step="any" value={form.price} onChange={e => set("price", e.target.value)} placeholder="175.50" data-testid="form-trade-price-input" className="bg-slate-900/70 border-slate-800 h-11 font-mono-tabular" />
            </Field>
            <Field label="Brokerage / Commission">
              <Input type="number" step="any" value={form.commission} onChange={e => set("commission", e.target.value)} data-testid="form-trade-commission-input" className="bg-slate-900/70 border-slate-800 h-11 font-mono-tabular" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Notes">
                <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} data-testid="form-trade-notes-input" className="bg-slate-900/70 border-slate-800" />
              </Field>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-xl border border-slate-800 bg-slate-900/40 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="label-caps">Estimated {form.trade_type === "BUY" ? "Outlay" : "Proceeds"}</div>
              <div className="metric-value text-2xl mt-1">{fmtMoney(netTotal)}</div>
              <div className="text-xs text-slate-500 mt-1">Gross {fmtMoney(gross)} · Commission {fmtMoney(commission)}</div>
            </div>
            <Button onClick={submit} disabled={submitting} data-testid="form-trade-submit-button"
              className={`h-12 px-8 font-bold text-base ${form.trade_type === "BUY" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"} text-slate-950`}>
              {submitting ? "Recording..." : `Record ${form.trade_type}`}
            </Button>
          </div>
        </div>

        <div className="card-tactical p-6 lg:col-span-4">
          <div className="label-caps">Latest 15</div>
          <h2 className="font-heading font-bold text-xl mt-1 mb-4">Recent Entries</h2>
          {recent.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-slate-800 rounded-lg">Trades will appear here.</div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="list-recent-trades">
              {recent.map(t => (
                <div key={t.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-800/60">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.trade_type === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{t.trade_type}</span>
                    <span className="font-mono-tabular font-bold text-sm">{t.symbol}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1.5">
                    {t.client_name}{t.account_name ? <span className="text-slate-500"> · {t.account_name}</span> : null}
                  </div>
                  <div className="text-xs font-mono-tabular mt-1 text-slate-300">{fmtNum(t.quantity, 2)} @ {fmtMoney(t.price)} · {t.trade_date}</div>
                  {t.created_by_username && <div className="text-[10px] text-slate-500 mt-1">by {t.created_by_username}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <BulkImport onImported={load} />

      <Dialog open={newStockOpen} onOpenChange={setNewStockOpen}>
        <DialogContent className="bg-[#0E1524] border-slate-800 text-white">
          <DialogHeader><DialogTitle className="font-heading">Add Stock to Database</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="label-caps !text-slate-400">Symbol *</Label>
              <Input value={newStock.symbol} onChange={e => setNewStock({ ...newStock, symbol: e.target.value.toUpperCase() })} placeholder="AAPL" data-testid="inline-new-stock-symbol" className="mt-1.5 uppercase font-mono-tabular" />
            </div>
            <div>
              <Label className="label-caps !text-slate-400">Company Name</Label>
              <Input value={newStock.name} onChange={e => setNewStock({ ...newStock, name: e.target.value })} placeholder="Apple Inc." data-testid="inline-new-stock-name" className="mt-1.5" />
            </div>
            <div>
              <Label className="label-caps !text-slate-400">Current Market Price</Label>
              <Input type="number" step="any" value={newStock.market_price} onChange={e => setNewStock({ ...newStock, market_price: e.target.value })} placeholder="175.50" data-testid="inline-new-stock-price" className="mt-1.5 font-mono-tabular" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewStockOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
            <Button onClick={saveNewStock} data-testid="btn-inline-save-stock" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">Add to Database</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-caps !text-slate-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
